// Shared content of the bug-report panel. Renders header + compose + my
// tickets + (conditionally) the dev-mode footer. Used by both the desktop
// floating pill (`BugReportButton`) and the mobile sandwich-menu modal
// (`BugReportMobileEntry`).
//
// The container chrome (positioning, sizing, close affordance) is owned
// by the caller — this component just stacks the inner sections.

import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { BugReportComposeBody } from "@/components/common/BugReportComposeBody";
import { BugReportMyTicketsList } from "@/components/common/BugReportMyTicketsList";
import {
  BugReportDevModeFooter,
  type BugReportDevModeSessionInfo,
} from "@/components/common/BugReportDevModeFooter";
import type { BugReport } from "@/components/common/bugReportClient";

export type BugReportPanelContentProps = {
  mode: PrimaryMode;
  description: string;
  onDescriptionChange: (next: string) => void;
  onDictationAppend: (chunk: string) => void;
  submitting: boolean;
  submitError: string | null;
  submitDone: boolean;
  submitDoneDevSync: boolean;
  canQueueSync: boolean;
  isOwnerSync: boolean;
  queueForDevSync: boolean;
  setQueueForDevSync: (next: boolean) => void;
  repoPath: string;
  setRepoPath: (next: string) => void;
  setGraphicFile: (file: File | null) => void;
  onSubmit: () => void;
  myReports: BugReport[];
  reportsLoading: boolean;
  openCount: number;
  /** When true, render the BugReportDevModeFooter with these callbacks. */
  devModeFooter: {
    devMode: boolean;
    sessionInfo: BugReportDevModeSessionInfo | null;
    onEnable: () => void;
    onDisable: () => void;
  } | null;
};

export function BugReportPanelContent(props: BugReportPanelContentProps) {
  return (
    <>
      <BugReportComposeBody
        mode={props.mode}
        description={props.description}
        onDescriptionChange={props.onDescriptionChange}
        onDictationAppend={props.onDictationAppend}
        submitting={props.submitting}
        submitError={props.submitError}
        submitDone={props.submitDone}
        submitDoneDevSync={props.submitDoneDevSync}
        canQueueSync={props.canQueueSync}
        isOwnerSync={props.isOwnerSync}
        queueForDevSync={props.queueForDevSync}
        setQueueForDevSync={props.setQueueForDevSync}
        repoPath={props.repoPath}
        setRepoPath={props.setRepoPath}
        setGraphicFile={props.setGraphicFile}
        onSubmit={props.onSubmit}
      />

      <BugReportMyTicketsList
        reports={props.myReports}
        reportsLoading={props.reportsLoading}
        openCount={props.openCount}
      />

      {props.devModeFooter ? (
        <BugReportDevModeFooter
          devMode={props.devModeFooter.devMode}
          sessionInfo={props.devModeFooter.sessionInfo}
          onEnable={props.devModeFooter.onEnable}
          onDisable={props.devModeFooter.onDisable}
        />
      ) : null}
    </>
  );
}
